import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// ── Supabase client (anon key only — safe in browser) ─────────
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const TYPES = [
  "Web Design & Development","Branding & Identity","Social Media Management",
  "SEO & Content Marketing","Video Production","Copywriting",
  "App Development","Consulting / Strategy","Photography","Other",
];

const C = {
  bg:"#faf7f2", bg2:"#f3ede3", bg3:"#ebe1d1", white:"#ffffff",
  ink:"#1c1811", inkMid:"#3d3328", inkSoft:"#6b5c47", inkFade:"#9c8b77",
  gold:"#b8883a", goldMid:"#d4a853", goldPale:"#f5e9d0",
  navy:"#1e2d4a", navyMid:"#2e4570",
  border:"#ddd0bc", borderSoft:"#ece4d8",
  green:"#2d6a4f", red:"#c0392b",
};


function Logo({ onClick }) {
  return (
    <div className="nav-logo" onClick={onClick}>
      <div className="nav-logo-icon">✍️</div>
      <div>
        <div className="nav-logo-name">SendPropel</div>
        <div className="nav-logo-sub">Proposal Generator</div>
      </div>
    </div>
  );
}

function StepDots({ step }) {
  const steps = [{ id:"form", label:"Details" }, { id:"loading", label:"Generate" }, { id:"result", label:"Result" }];
  const idx = steps.findIndex(s => s.id === step);
  return (
    <div className="step-dots">
      {steps.map((s, i) => (
        <div key={s.id} style={{ display:"flex", alignItems:"center", gap:10 }}>
          {i > 0 && <div style={{ width:22, height:1, background:"#ddd0bc" }} />}
          <div style={{ display:"flex", alignItems:"center", gap:7, opacity:i <= idx ? 1 : 0.38, transition:"opacity 0.3s" }}>
            <div className={`step-dot ${i < idx ? "step-dot-done" : i === idx ? "step-dot-active" : "step-dot-idle"}`}>
              {i < idx ? "✓" : i + 1}
            </div>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", fontFamily:"'Libre Baskerville',serif", color:i === idx ? "#1c1811" : "#9c8b77" }}>{s.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function TypewriterText({ text, speed = 8, onDone }) {
  const [out, setOut] = useState("");
  const [done, setDone] = useState(false);
  const i = useRef(0);
  useEffect(() => {
    setOut(""); setDone(false); i.current = 0;
    const iv = setInterval(() => {
      if (i.current < text.length) { setOut(text.slice(0, i.current + 1)); i.current++; }
      else { clearInterval(iv); setDone(true); onDone?.(); }
    }, speed);
    return () => clearInterval(iv);
  }, [text]);
  return <span>{out}{!done && <span style={{ borderRight:"2px solid #b8883a", marginLeft:1, animation:"blink .7s steps(1) infinite" }}>&nbsp;</span>}</span>;
}

function FollowUpCard({ day, subject, body, index }) {
  const [open, setOpen] = useState(false);
  const pals = [
    { bg:"#f0f4ff", border:"#c7d4f5", accent:"#2e4570", label:"First nudge" },
    { bg:"#fff8f0", border:"#f5ddb8", accent:"#b8883a", label:"Check in"    },
    { bg:"#f0faf4", border:"#b8dfc8", accent:"#2d6a4f", label:"Final touch" },
  ];
  const p = pals[index];
  return (
    <div className="fu-card" onClick={() => setOpen(!open)} style={{ background:open ? p.bg : "#fff", border:`1.5px solid ${open ? p.border : "#ece4d8"}`, boxShadow:open ? `0 4px 18px ${p.accent}18` : "0 1px 4px rgba(0,0,0,0.05)" }}>
      <div style={{ display:"flex", alignItems:"center", gap:14 }}>
        <div style={{ width:44, height:44, borderRadius:11, flexShrink:0, background:p.bg, border:`2px solid ${p.border}`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
          <span style={{ fontSize:8, fontWeight:700, color:p.accent, letterSpacing:"0.1em" }}>DAY</span>
          <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:p.accent, lineHeight:1 }}>{day}</span>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <span style={{ fontSize:9, fontWeight:700, color:p.accent, letterSpacing:"0.1em", textTransform:"uppercase", background:`${p.accent}14`, padding:"2px 9px", borderRadius:20 }}>{p.label}</span>
          <div style={{ fontWeight:700, fontSize:14, color:"#1c1811", marginTop:5, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{subject}</div>
        </div>
        <div style={{ color:"#9c8b77", fontSize:15, transform:open ? "rotate(180deg)" : "none", transition:"0.2s", flexShrink:0 }}>▾</div>
      </div>
      {open && (
        <div style={{ marginTop:14, padding:"16px 18px", background:"#fff", borderRadius:10, border:`1px solid ${p.border}`, fontSize:14, color:"#3d3328", lineHeight:1.85, whiteSpace:"pre-wrap" }}>
          {body}
        </div>
      )}
    </div>
  );
}

function AuthModal({ onClose, onSuccess }) {
  const [mode, setMode]     = useState("login");
  const [email, setEmail]   = useState("");
  const [password, setPass] = useState("");
  const [loading, setLoad]  = useState(false);
  const [msg, setMsg]       = useState("");
  const [err, setErr]       = useState("");

  const submit = async () => {
    if (!email.includes("@")) { setErr("Please enter a valid email."); return; }
    setLoad(true); setErr(""); setMsg("");
    try {
      if (mode === "magic") {
        const { error } = await supabase.auth.signInWithOtp({ email, options: { emailRedirectTo: window.location.origin } });
        if (error) throw error;
        setMsg("Check your inbox — we sent you a magic link.");
      } else if (mode === "signup") {
        if (password.length < 8) { setErr("Password must be at least 8 characters."); setLoad(false); return; }
        const { error } = await supabase.auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin } });
        if (error) throw error;
        setMsg("Check your inbox to confirm your email, then log in.");
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onSuccess(data.session);
      }
    } catch (e) { setErr(e.message); }
    setLoad(false);
  };

  return (
    <div className="modal-bg">
      <div className="modal">
        <button onClick={onClose} style={{ position:"absolute", top:16, right:20, background:"none", border:"none", color:"#9c8b77", fontSize:24, cursor:"pointer" }}>×</button>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ width:52, height:52, borderRadius:14, background:"#f5e9d0", border:"2px solid rgba(184,136,58,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, margin:"0 auto 14px" }}>✍️</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:"#1c1811", marginBottom:6 }}>
            {mode === "login" ? "Welcome Back" : mode === "signup" ? "Create Your Account" : "Sign In with Email Link"}
          </h2>
        </div>
        <div style={{ display:"flex", background:"#f3ede3", border:"1px solid #ddd0bc", borderRadius:10, padding:3, marginBottom:20 }}>
          {[["login","Log In"],["signup","Sign Up"],["magic","Magic Link"]].map(([id, lbl]) => (
            <button key={id} onClick={() => { setMode(id); setErr(""); setMsg(""); }} style={{ flex:1, padding:"8px", borderRadius:8, border:"none", cursor:"pointer", fontFamily:"'Libre Baskerville',serif", fontWeight:700, fontSize:12, transition:"all 0.18s", background:mode === id ? "#fff" : "transparent", color:mode === id ? "#1c1811" : "#9c8b77", boxShadow:mode === id ? "0 1px 4px rgba(0,0,0,0.08)" : "none" }}>{lbl}</button>
          ))}
        </div>
        <label className="lbl">Email</label>
        <input className="field" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={{ marginBottom:14 }} />
        {(mode === "login" || mode === "signup") && (
          <>
            <label className="lbl">Password</label>
            <input className="field" type="password" value={password} onChange={e => setPass(e.target.value)} placeholder="••••••••" style={{ marginBottom:20 }} onKeyDown={e => e.key === "Enter" && submit()} />
          </>
        )}
        {err && <div className="alert-error">⚠ {err}</div>}
        {msg && <div style={{ background:"#f0faf4", border:"1px solid rgba(45,106,79,0.4)", borderRadius:10, padding:"11px 15px", color:"#2d6a4f", fontSize:13, marginBottom:14 }}>{msg}</div>}
        {!msg && (
          <button className="btn btn-navy" onClick={submit} disabled={loading} style={{ width:"100%", padding:"14px", fontSize:14, borderRadius:12, justifyContent:"center" }}>
            {loading ? "Please wait…" : mode === "login" ? "Log In" : mode === "signup" ? "Create Account" : "Send Magic Link"}
          </button>
        )}
        <div style={{ textAlign:"center", marginTop:14, fontSize:12, color:"#9c8b77" }}>By signing up you agree to our Terms & Privacy Policy.</div>
      </div>
    </div>
  );
}

function PaywallModal({ session, onClose }) {
  const [loading, setLoad] = useState(false);
  const [err, setErr]      = useState("");

  const checkout = async () => {
    setLoad(true); setErr("");
    try {
      const res = await fetch("/api/create-checkout", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "Authorization":`Bearer ${session.access_token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to start checkout");
      window.location.href = data.url;
    } catch (e) { setErr(e.message); setLoad(false); }
  };

  return (
    <div className="modal-bg">
      <div className="modal">
        <button onClick={onClose} style={{ position:"absolute", top:16, right:20, background:"none", border:"none", color:"#9c8b77", fontSize:24, cursor:"pointer" }}>×</button>
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ width:54, height:54, borderRadius:15, background:"#f5e9d0", border:"2px solid rgba(184,136,58,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, margin:"0 auto 14px" }}>🔓</div>
          <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:28, fontWeight:700, color:"#1c1811", marginBottom:8, lineHeight:1.2 }}>Unlock Unlimited Proposals</h2>
          <p style={{ color:"#6b5c47", fontSize:14, lineHeight:1.7 }}>You've used your free proposal. Upgrade to keep winning clients.</p>
        </div>
        <div style={{ background:"#f3ede3", border:"1.5px solid #ddd0bc", borderRadius:14, padding:"20px 22px", marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
            <div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:38, fontWeight:700, color:"#1c1811", lineHeight:1 }}>$29</div>
              <div style={{ fontSize:12, color:"#6b5c47", marginTop:2 }}>per month · cancel anytime</div>
            </div>
            <div style={{ background:"#b8883a", color:"#fff", fontSize:10, fontWeight:700, padding:"4px 12px", borderRadius:20, letterSpacing:"0.07em" }}>PRO PLAN</div>
          </div>
          {["Unlimited AI proposals","3 follow-up emails per proposal","All project types","Cancel anytime"].map(f => (
            <div key={f} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <span style={{ color:"#2d6a4f", fontWeight:700 }}>✓</span>
              <span style={{ fontSize:13, color:"#3d3328" }}>{f}</span>
            </div>
          ))}
        </div>
        {err && <div className="alert-error">{err}</div>}
        <button className="btn btn-navy" onClick={checkout} disabled={loading} style={{ width:"100%", padding:"15px", fontSize:14, borderRadius:12, justifyContent:"center" }}>
          {loading ? "Redirecting to Stripe…" : "Upgrade — $29/mo"}
        </button>
        <div style={{ textAlign:"center", marginTop:9, fontSize:11, color:"#9c8b77" }}>Secure payment via Stripe · Cancel anytime</div>
      </div>
    </div>
  );
}

function Landing({ go, session, onAuth, onSignOut }) {
  return (
    <div style={{ background:"#faf7f2", minHeight:"100vh" }} className="page-texture">
      <nav className="site-nav">
        <Logo />
        <div className="nav-links">
          <a className="nav-link" href="#how">How it works</a>
          <a className="nav-link" href="#features">Features</a>
          <a className="nav-link" href="#pricing">Pricing</a>
          {session ? (
            <>
              <span style={{ fontSize:12, color:"#9c8b77" }}>{session.user.email}</span>
              <button className="btn btn-outline btn-sm" onClick={onSignOut}>Sign Out</button>
              <button className="btn btn-navy" style={{ padding:"10px 24px", fontSize:13 }} onClick={go}>Open App →</button>
            </>
          ) : (
            <>
              <button className="btn btn-outline btn-sm" onClick={onAuth}>Log In</button>
              <button className="btn btn-navy" style={{ padding:"10px 24px", fontSize:13 }} onClick={go}>Try Free →</button>
            </>
          )}
        </div>
      </nav>
      <section style={{ textAlign:"center", padding:"96px 24px 72px", maxWidth:820, margin:"0 auto" }}>
        <div className="hero-pill">✦ AI-powered · Generates in ~10 seconds</div>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(40px,7vw,70px)", fontWeight:700, lineHeight:1.08, color:"#1c1811", marginBottom:22 }}>
          Stop Losing Clients to<br /><span className="shimmer-text">Weak Proposals</span>
        </h1>
        <p style={{ color:"#6b5c47", fontSize:17, lineHeight:1.8, maxWidth:520, margin:"0 auto 38px", fontStyle:"italic" }}>
          SendPropel writes polished, client-specific proposals and three follow-up emails in about 10 seconds.
        </p>
        <div style={{ display:"flex", justifyContent:"center", gap:14, flexWrap:"wrap", marginBottom:60 }}>
          <button className="btn btn-navy" onClick={go} style={{ padding:"15px 42px", fontSize:15 }}>Write My First Proposal — Free</button>
          <button className="btn btn-outline" onClick={() => document.getElementById("how").scrollIntoView({ behavior:"smooth" })} style={{ padding:"15px 28px", fontSize:14 }}>See how it works ↓</button>
        </div>
        <div style={{ display:"flex", justifyContent:"center", borderTop:"1px solid #ddd0bc", borderBottom:"1px solid #ddd0bc", padding:"26px 0", flexWrap:"wrap" }}>
          {[["1,200+","Freelancers"],["~10 sec","To generate"],["3×","Follow-ups"],["$29/mo","Pro plan"]].map(([stat, lbl], i, arr) => (
            <div key={lbl} style={{ flex:"1 1 140px", textAlign:"center", padding:"0 20px", borderRight:i < arr.length - 1 ? "1px solid #ddd0bc" : "none" }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34, fontWeight:700, color:"#1e2d4a", lineHeight:1 }}>{stat}</div>
              <div style={{ fontSize:11, color:"#6b5c47", marginTop:5 }}>{lbl}</div>
            </div>
          ))}
        </div>
      </section>
      <section id="how" style={{ padding:"70px 24px", maxWidth:920, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:50 }}>
          <div className="section-eyebrow">How it works</div>
          <h2 className="section-h2">Three Steps to a Winning Proposal</h2>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(250px,1fr))", gap:20 }}>
          {[
            { n:"I",   icon:"📝", title:"Describe the Project",    body:"Enter your client's name, project type, budget, and pain points in under two minutes." },
            { n:"II",  icon:"🤖", title:"AI Writes Your Proposal", body:"SendPropel crafts a warm, persuasive proposal tailored to your client — never a generic template." },
            { n:"III", icon:"📩", title:"Send & Follow Up",         body:"Copy your proposal and use the three pre-written follow-up emails at Day 3, 7, and 14 to close the deal." },
          ].map(({ n, icon, title, body }) => (
            <div key={n} className="feat-card" style={{ borderTop:"4px solid #b8883a" }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:13, fontWeight:600, color:"#b8883a", letterSpacing:"0.15em", marginBottom:14 }}>{n}</div>
              <div style={{ fontSize:28, marginBottom:14 }}>{icon}</div>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:21, fontWeight:700, color:"#1c1811", marginBottom:10 }}>{title}</h3>
              <p style={{ color:"#6b5c47", fontSize:14, lineHeight:1.78 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>
      <section id="features" style={{ padding:"70px 24px", maxWidth:920, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:50 }}>
          <div className="section-eyebrow">Features</div>
          <h2 className="section-h2">Everything You Need to Win More Work</h2>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:18 }}>
          {[
            { icon:"🎯", title:"Truly Personalized",   body:"References the client's name, industry, and exact pain points every time." },
            { icon:"📩", title:"3 Follow-Up Emails",   body:"Pre-written Day 3, 7 & 14 emails — automated follow-through, every time." },
            { icon:"⚡", title:"Ready in ~10 Seconds", body:"From blank page to polished proposal in under a minute." },
            { icon:"📋", title:"10 Project Types",     body:"Web, branding, SEO, video, copy, consulting, photography and more." },
            { icon:"📥", title:"One-Click Copy",       body:"Copy the entire proposal to clipboard and paste anywhere." },
            { icon:"🔄", title:"Unlimited on Pro",     body:"Pro plan removes all limits — pitch as many clients as you want." },
          ].map(({ icon, title, body }) => (
            <div key={title} className="feat-card">
              <div style={{ fontSize:26, marginBottom:14 }}>{icon}</div>
              <h3 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontWeight:700, color:"#1c1811", marginBottom:8 }}>{title}</h3>
              <p style={{ color:"#6b5c47", fontSize:13.5, lineHeight:1.78 }}>{body}</p>
            </div>
          ))}
        </div>
      </section>
      <section style={{ padding:"70px 24px", maxWidth:920, margin:"0 auto" }}>
        <div style={{ textAlign:"center", marginBottom:48 }}>
          <div className="section-eyebrow">Testimonials</div>
          <h2 className="section-h2">Freelancers Trust SendPropel</h2>
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(260px,1fr))", gap:18 }}>
          {[
            { name:"Sarah K.",  role:"Brand Designer",      q:"I used to spend two hours on every proposal. Now it takes ten minutes. I've already closed three new clients this month." },
            { name:"Marcus T.", role:"Web Developer",        q:"The follow-up emails are pure gold. I never remembered to follow up before. My close rate has genuinely doubled." },
            { name:"Priya N.",  role:"Social Media Manager", q:"The proposals sound like me — not a generic AI. Several clients specifically commented on how professional they look." },
          ].map(({ name, role, q }) => (
            <div key={name} className="testi-card">
              <div style={{ color:"#b8883a", fontSize:16, marginBottom:14, letterSpacing:"0.1em" }}>★★★★★</div>
              <p style={{ color:"#3d3328", fontSize:14, lineHeight:1.82, marginBottom:18, fontStyle:"italic" }}>"{q}"</p>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontWeight:700, fontSize:17, color:"#1c1811" }}>{name}</div>
              <div style={{ fontSize:10, color:"#9c8b77", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginTop:2 }}>{role}</div>
            </div>
          ))}
        </div>
      </section>
      <section id="pricing" style={{ padding:"70px 24px 80px", maxWidth:480, margin:"0 auto", textAlign:"center" }}>
        <div className="section-eyebrow">Pricing</div>
        <h2 className="section-h2" style={{ marginBottom:10 }}>Simple, Honest Pricing</h2>
        <p style={{ color:"#6b5c47", fontSize:15, lineHeight:1.7, marginBottom:42, fontStyle:"italic" }}>One plan. Everything included. Cancel anytime.</p>
        <div className="price-card">
          <div style={{ display:"inline-block", background:"#1e2d4a", color:"#fff", fontSize:10, fontWeight:700, padding:"4px 14px", borderRadius:20, letterSpacing:"0.09em", marginBottom:18 }}>SENDPROPEL PRO</div>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:64, fontWeight:700, color:"#1c1811", lineHeight:1 }}>$29</div>
          <div style={{ color:"#6b5c47", fontSize:14, marginBottom:28, fontStyle:"italic" }}>per month · cancel anytime</div>
          <div style={{ marginBottom:28 }}>
            {["Unlimited AI proposals","3 follow-up emails per proposal","All 10 project types","One-click copy & export","Priority support","New templates monthly"].map(f => (
              <div key={f} className="check-row">
                <span style={{ color:"#2d6a4f", fontWeight:700, flexShrink:0 }}>✓</span>
                <span style={{ fontSize:14, color:"#3d3328" }}>{f}</span>
              </div>
            ))}
          </div>
          <button className="btn btn-navy" onClick={go} style={{ width:"100%", padding:"15px", fontSize:14, justifyContent:"center", borderRadius:12 }}>Start Free — Then $29/mo</button>
          <div style={{ marginTop:11, fontSize:12, color:"#9c8b77", fontStyle:"italic" }}>First proposal free · No credit card required</div>
        </div>
      </section>
      <section style={{ textAlign:"center", padding:"56px 24px 72px", borderTop:"1px solid #ddd0bc", background:"#f3ede3" }}>
        <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(26px,4.5vw,44px)", fontWeight:700, color:"#1c1811", marginBottom:14 }}>Ready to Win More Clients?</h2>
        <p style={{ color:"#6b5c47", fontSize:16, lineHeight:1.7, maxWidth:400, margin:"0 auto 28px", fontStyle:"italic" }}>Join 1,200+ freelancers closing more deals with SendPropel.</p>
        <button className="btn btn-navy" onClick={go} style={{ padding:"15px 44px", fontSize:15 }}>Try It Free Now</button>
        <div style={{ marginTop:48, fontSize:12, color:"#9c8b77" }}>2025 SendPropel · Built for freelancers</div>
        <div style={{ marginTop:10, fontSize:12, color:"#9c8b77" }}>
          Need help? Email us at <a href="mailto:support@sendpropel.com" style={{ color:"#b8883a", textDecoration:"none", fontWeight:700 }}>support@sendpropel.com</a>
        </div>
      </section>
    </div>
  );
}

function AppScreen({ session, onSignOut, goLanding, isPro, freeLeft, refreshProfile }) {
  const [step, setStep]         = useState("form");
  const [form, setForm]         = useState({ yourName:"", yourRole:"", clientName:"", projectType:"", budget:"", timeline:"", clientNeeds:"" });
  const [proposal, setProposal] = useState("");
  const [followUps, setFU]      = useState([]);
  const [typed, setTyped]       = useState(false);
  const [tab, setTab]           = useState("proposal");
  const [copied, setCopied]     = useState(false);
  const [error, setError]       = useState("");
  const [dots, setDots]         = useState(0);
  const [showPaywall, setPaywall] = useState(false);

  useEffect(() => {
    if (step !== "loading") return;
    const iv = setInterval(() => setDots(d => (d + 1) % 4), 500);
    return () => clearInterval(iv);
  }, [step]);

  const set = e => setForm({ ...form, [e.target.name]: e.target.value });

  const generate = async () => {
    if (!form.yourName || !form.clientName || !form.projectType) {
      setError("Please fill in Your Name, Client Name, and Project Type."); return;
    }
    if (!isPro && freeLeft <= 0) { setPaywall(true); return; }
    setError(""); setStep("loading");
    try {
      const res = await fetch("/api/generate", {
        method:"POST",
        headers:{ "Content-Type":"application/json", "Authorization":`Bearer ${session.access_token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 402) { setPaywall(true); setStep("form"); return; }
        throw new Error(data.error || "Generation failed");
      }
      setProposal(data.proposal); setFU(data.followUps);
      setTab("proposal"); setTyped(false);
      await refreshProfile();
      setStep("result");
    } catch (e) { setError(e.message); setStep("form"); }
  };

  const copy = t => { navigator.clipboard.writeText(t); setCopied(true); setTimeout(() => setCopied(false), 2200); };
  const reset = () => { setStep("form"); setProposal(""); setFU([]); setTyped(false); setError(""); };

  return (
    <div style={{ background:"#faf7f2", minHeight:"100vh" }} className="page-texture">
      {showPaywall && session && <PaywallModal session={session} onClose={() => setPaywall(false)} />}
      <nav className="site-nav">
        <Logo onClick={goLanding} />
        <StepDots step={step} />
        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
          {isPro && <span className="pro-badge">PRO ✓</span>}
          <span style={{ fontSize:11, color:"#9c8b77" }}>{session?.user?.email}</span>
          <button className="btn btn-outline btn-sm" onClick={onSignOut}>Sign Out</button>
        </div>
      </nav>
      <div style={{ maxWidth:720, margin:"0 auto", padding:"48px 24px 100px" }}>
        {step === "form" && (
          <div className="fu">
            <div style={{ textAlign:"center", marginBottom:44 }}>
              <div className="hero-pill">
                {isPro ? "✦ Pro — unlimited proposals" : freeLeft > 0 ? `✦ ${freeLeft} free proposal remaining` : "⚠ Free proposal used — upgrade to continue"}
              </div>
              <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"clamp(30px,5.5vw,50px)", fontWeight:700, lineHeight:1.12, color:"#1c1811", marginBottom:14 }}>
                Write Proposals That<br /><em style={{ color:"#1e2d4a" }}>Actually Win Clients</em>
              </h1>
              <p style={{ color:"#6b5c47", fontSize:15, lineHeight:1.8, maxWidth:440, margin:"0 auto", fontStyle:"italic" }}>
                Fill in a few details and receive a personalized proposal with three follow-up emails in about 10 seconds.
              </p>
            </div>
            <div className="card">
              {error && <div className="alert-error">⚠ {error}</div>}
              <div className="card-tag">👤 About You</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
                <div><label className="lbl">Your Name <span className="lbl-req">*</span></label><input className="field" name="yourName" placeholder="e.g. Jordan Lee" value={form.yourName} onChange={set} /></div>
                <div><label className="lbl">Your Role</label><input className="field" name="yourRole" placeholder="e.g. Brand Designer" value={form.yourRole} onChange={set} /></div>
              </div>
              <div className="rule" />
              <div className="card-tag">📋 The Project</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
                <div><label className="lbl">Client / Company <span className="lbl-req">*</span></label><input className="field" name="clientName" placeholder="e.g. Maple & Co Studio" value={form.clientName} onChange={set} /></div>
                <div>
                  <label className="lbl">Project Type <span className="lbl-req">*</span></label>
                  <select className="field" name="projectType" value={form.projectType} onChange={set}>
                    <option value="">Select a type…</option>
                    {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:16 }}>
                <div><label className="lbl">Budget Range (USD)</label><input className="field" name="budget" placeholder="e.g. 2,500 – 4,000" value={form.budget} onChange={set} /></div>
                <div><label className="lbl">Timeline</label><input className="field" name="timeline" placeholder="e.g. 3–4 weeks" value={form.timeline} onChange={set} /></div>
              </div>
              <div style={{ marginBottom:30 }}>
                <label className="lbl">Client Needs & Pain Points</label>
                <textarea className="field" name="clientNeeds" placeholder="What is the client struggling with? What outcome do they want? The more context, the more persuasive…" value={form.clientNeeds} onChange={set} rows={4} style={{ resize:"vertical", lineHeight:1.7 }} />
              </div>
              <div style={{ textAlign:"center" }}>
                <button className="btn btn-navy" onClick={generate} style={{ padding:"15px 44px", fontSize:14, borderRadius:12 }}>Generate My Proposal & Follow-Ups</button>
                <div style={{ marginTop:10, fontSize:12, color:"#9c8b77", fontStyle:"italic" }}>Takes approximately 10 seconds</div>
              </div>
            </div>
          </div>
        )}
        {step === "loading" && (
          <div className="fu" style={{ textAlign:"center", padding:"110px 0" }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:28 }}><div className="spinner" /></div>
            <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:30, fontWeight:700, color:"#1c1811", marginBottom:10 }}>Crafting your proposal{".".repeat(dots)}</h2>
            <p style={{ color:"#6b5c47", fontSize:14, fontStyle:"italic" }}>Reading client needs · Writing your story · Building follow-ups</p>
          </div>
        )}
        {step === "result" && (
          <div className="fu">
            <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", marginBottom:30, flexWrap:"wrap", gap:12 }}>
              <div>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
                  <div style={{ width:8, height:8, borderRadius:"50%", background:"#2d6a4f" }} />
                  <span style={{ fontSize:10, fontWeight:700, color:"#2d6a4f", letterSpacing:"0.1em", textTransform:"uppercase" }}>Ready to send</span>
                </div>
                <h2 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:700, color:"#1c1811" }}>Proposal for <em style={{ color:"#1e2d4a" }}>{form.clientName}</em></h2>
                <p style={{ color:"#9c8b77", fontSize:13, marginTop:4 }}>{form.projectType}</p>
              </div>
              <button className="btn btn-outline" onClick={reset}>← New Proposal</button>
            </div>
            <div className="tabs-wrap" style={{ marginBottom:24 }}>
              {[["proposal","📄  Proposal"],["followups","📩  Follow-Ups"]].map(([id, lbl]) => (
                <button key={id} onClick={() => setTab(id)} className={`tab ${tab === id ? "tab-on" : "tab-off"}`}>{lbl}</button>
              ))}
            </div>
            {tab === "proposal" && (
              <div className="card fu">
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:"#9c8b77", letterSpacing:"0.12em", textTransform:"uppercase" }}>Proposal Document</span>
                  <button onClick={() => copy(proposal)} className={`copy-btn ${copied ? "copy-btn-done" : ""}`}>{copied ? "✓ Copied!" : "Copy Text"}</button>
                </div>
                <div className="rule" style={{ margin:"0 0 18px" }} />
                <div style={{ fontSize:15, lineHeight:1.9, color:"#3d3328", whiteSpace:"pre-wrap" }}>
                  {!typed ? <TypewriterText text={proposal} speed={7} onDone={() => setTyped(true)} /> : proposal}
                </div>
              </div>
            )}
            {tab === "followups" && (
              <div className="fu">
                <div className="alert-info" style={{ marginBottom:16 }}>
                  <strong style={{ color:"#1e2d4a" }}>Why follow-ups win:</strong> 80% of deals happen after multiple contacts. Most freelancers give up after one. These three emails put you ahead of the competition.
                </div>
                {followUps.map((fu, i) => <FollowUpCard key={i} index={i} day={fu.day} subject={fu.subject} body={fu.body} />)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Root() {
  const [screen, setScreen]     = useState("landing");
  const [session, setSession]   = useState(null);
  const [showAuth, setAuth]     = useState(false);
  const [isPro, setPro]         = useState(false);
  const [freeLeft, setFreeLeft] = useState(1);
  const [booting, setBooting]   = useState(true);

  const loadProfile = async (sess) => {
    if (!sess) return;
    try {
      const res = await fetch("/api/me", { headers:{ "Authorization":`Bearer ${sess.access_token}` } });
      if (res.ok) { const d = await res.json(); setPro(d.isPro); setFreeLeft(d.freeUsesRemaining); }
    } catch {}
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s); setBooting(false);
      if (s) loadProfile(s);
      if (window.location.search.includes("session_id=")) {
        setScreen("app");
        window.history.replaceState({}, "", window.location.pathname);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s);
      if (s) { loadProfile(s); setAuth(false); }
      else { setPro(false); setFreeLeft(1); }
    });
    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => { await supabase.auth.signOut(); setSession(null); setPro(false); setFreeLeft(1); setScreen("landing"); };
  const goApp   = () => { if (!session) { setAuth(true); return; } setScreen("app"); };

  if (booting) return <div style={{ background:"#faf7f2", minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center" }}><div className="spinner" /></div>;

  return (
    <>
      {showAuth && <AuthModal onClose={() => setAuth(false)} onSuccess={(s) => { setSession(s); setScreen("app"); }} />}
      {screen === "landing"
        ? <Landing go={goApp} session={session} onAuth={() => setAuth(true)} onSignOut={signOut} />
        : <AppScreen session={session} onSignOut={signOut} goLanding={() => setScreen("landing")} isPro={isPro} freeLeft={freeLeft} refreshProfile={() => loadProfile(session)} />
      }
    </>
  );
}